import React from "react";
import { useLocation } from "react-router-dom";
import { Modal, Button, Alert } from "react-bootstrap";
const CookiePolicy = "/documents/Vesseli_International_Ltd_-_cookie_policy.docx";

const LazyFileViewer = React.lazy(() => import("react-file-viewer"));

const Cookies = () => {
  const [showBanner, setShowBanner] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [viewerError, setViewerError] = React.useState(false);
  const [useFallback, setUseFallback] = React.useState(false);
  const [modalHeight, setModalHeight] = React.useState(450);
  const location = useLocation();

  // Helper functions
  const setCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/`;
  };

  const getCookie = (name) => {
    const cookies = document.cookie.split("; ");
    for (let cookie of cookies) {
      const [key, val] = cookie.split("=");
      if (key === name) return val;
    }
    return null;
  };
  
  // Check on load if user already gave consent
  React.useEffect(() => {
    // keep showBanner in sync for cases when component remains mounted and cookie changes
    setShowBanner(getCookie("cookieConsent") !== "accepted");
  }, []);

  React.useEffect(() => {
    // Adjust viewer height based on window size
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      // Make modal body smaller on smaller screens
      if (windowHeight < 768) {
        setModalHeight(300);
      } else if (windowHeight < 900) {
        setModalHeight(400);
      } else {
        setModalHeight(450);
      }
    };

    handleResize(); // Set initial size
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleConsent = (accepted) => {
    if (accepted) {
      // Persist acceptance so banner won't show again
      setCookie("cookieConsent", "accepted", 365);
    }
    // Hide banner for this session (if declined, it will reappear next time)
    setShowBanner(false);
  };

  const handleOpenModal = (e) => {
    e.preventDefault();
    setViewerError(false);
    setUseFallback(false);
    setShowModal(true);

    // Reset scroll position of document viewer when opening
    setTimeout(() => {
      const container = window.document.querySelector(
        ".document-viewer-container"
      );
      if (container) {
        container.scrollTop = 0;
      }
    }, 100);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setViewerError(false);
    setUseFallback(false);
  };

  const onError = (e) => {
    console.log("Error in file viewer:", e);
    setViewerError(true);
  };

  const tryFallbackViewer = () => {
    setUseFallback(true);
    setViewerError(false);
  };

  // after all hooks/effects and helper functions compute route/cookie and decide render
  const onLoginOrSignup = location.pathname === "/login" || location.pathname === "/signup";
  const consent = getCookie("cookieConsent");

  // If not on login/signup OR already accepted -> render nothing
  if (!onLoginOrSignup || consent === "accepted") return null;

  return (
    <>
      <style jsx="true">{`
        .document-modal .modal-dialog {
          max-width: 90%;
          margin: 10px auto;
        }
        .document-modal .modal-content {
          max-height: calc(100vh - 60px);
          display: flex;
          flex-direction: column;
        }
        .document-modal .modal-body {
          flex: 1;
          overflow: hidden;
        }
        @media (max-width: 768px) {
          .document-modal .modal-dialog {
            max-width: 95%;
            margin: 5px auto;
          }
        }
      `}</style>
      <div className="cookies w-100 cookie-banner p-2">
        <p className="text-sm ps-lg-4 cookie_text">
          We use cookies for a number of reasons, such as keeping our sites
          reliable and secure, personalizing content and ads, providing
          social media features, and analyzing how our sites are used.
          &nbsp;
          <a
            style={{
              color: "rgb(0 171 251)",
              textDecoration: "underline",
            }}
            href="#"
            onClick={handleOpenModal}
          >
            read more
          </a>
        </p>
        <div className="d-flex cookie_btns_block">
          <button
            className="btn btn-blue ms-3"
            style={{ padding: "3px 13px" }}
            onClick={() => handleConsent(true)}
          >
            Accept
          </button>

          <button
            className="btn btn-white0 ms-3 me-2"
            onClick={() => handleConsent(false)}
          >
            Decline
          </button>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        dialogClassName="document-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Cookie Policy</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "10px", overflow: "hidden" }}>
          <div
            className="document-viewer-container"
            style={{
              height: `${modalHeight}px`,
              overflow: "auto",
              marginBottom: "10px",
            }}
          >
            {viewerError ? (
              <Alert variant="warning">
                <Alert.Heading>Unable to preview document</Alert.Heading>
                <p>
                  We're having trouble displaying this document in the browser.
                  You can try our alternative viewer or download the document.
                </p>
                <div className="d-flex justify-content-end">
                  <Button
                    variant="outline-primary"
                    onClick={tryFallbackViewer}
                  >
                    Try Alternative Viewer
                  </Button>
                </div>
              </Alert>
            ) : useFallback ? (
              <iframe
                src={`https://docs.google.com/gview?url=${encodeURIComponent(
                  window.location.origin + CookiePolicy
                )}&embedded=true`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                title="Cookie Policy"
                frameBorder="0"
              />
            ) : (
              <React.Suspense fallback={<div className="text-center p-4">Loading document...</div>}>
                <LazyFileViewer
                  fileType="docx"
                  filePath={CookiePolicy}
                  onError={onError}
                />
              </React.Suspense>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" href={CookiePolicy} download>
            Download
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Cookies;
